import { useMutation, type DefaultError, type UseMutationResult } from '@tanstack/react-query';

import { TaskCreationParams, TaskStatus, TaskUpdateParams } from 'chaika-frontend-types/frontend-cms';

import { TaskApi } from '@api/TaskApi';
import { queryClient } from '@store/queryClient';

import { taskKeys } from '@common/Task/queries/taskQueries';

import {
    syncCreatedTaskToStore,
    syncTaskCompletedToStore,
    syncTaskStatusToStore,
    syncTaskUpdateToStore,
} from './taskStoreBridge';

interface UpdateVariables {
    id: string;
    params: TaskUpdateParams;
}

interface StatusVariables {
    id: string;
}

export function useCreateTask(): UseMutationResult<void, DefaultError, TaskCreationParams> {
    return useMutation({
        mutationFn: (params: TaskCreationParams) => TaskApi.create(params),
        onSuccess: (_data, params) => {
            syncCreatedTaskToStore(params);
            void queryClient.invalidateQueries({ queryKey: taskKeys.all });
        },
    });
}

export function useUpdateTask(): UseMutationResult<void, DefaultError, UpdateVariables> {
    return useMutation({
        mutationFn: ({ id, params }: UpdateVariables) => TaskApi.update(id, params),
        onSuccess: (_data, { id, params }) => {
            syncTaskUpdateToStore(id, params);
            void queryClient.invalidateQueries({ queryKey: taskKeys.detail(id) });
            void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
        },
    });
}

export function useDeleteTask(): UseMutationResult<void, DefaultError, StatusVariables> {
    return useMutation({
        mutationFn: ({ id }: StatusVariables) => TaskApi.updateStatus(id, TaskStatus.Cancelled),
        onSuccess: (_data, { id }) => {
            syncTaskStatusToStore(id, TaskStatus.Cancelled);
            void queryClient.invalidateQueries({ queryKey: taskKeys.detail(id) });
            void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
        },
    });
}

export function useCompleteTask(): UseMutationResult<void, DefaultError, StatusVariables> {
    return useMutation({
        mutationFn: ({ id }: StatusVariables) => TaskApi.updateStatus(id, TaskStatus.Completed),
        onSuccess: (_data, { id }) => {
            syncTaskCompletedToStore(id);
            void queryClient.invalidateQueries({ queryKey: taskKeys.detail(id) });
            void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
        },
    });
}

export function useCancelTaskExecution(): UseMutationResult<void, DefaultError, StatusVariables> {
    return useMutation({
        mutationFn: ({ id }: StatusVariables) => TaskApi.updateStatus(id, TaskStatus.Requested),
        onSuccess: (_data, { id }) => {
            syncTaskStatusToStore(id, TaskStatus.Requested);
            void queryClient.invalidateQueries({ queryKey: taskKeys.detail(id) });
            void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
        },
    });
}
